"use client";

import { useRef } from "react";
import { ChevronLeft, ChevronRight, Quote, Star } from "lucide-react";

export type Testimonial = {
  rating: number;
  content: string;
  email: string;
};

function maskEmail(email: string) {
  const [name, domain] = email.split("@");
  if (!name || !domain) return email;
  const visible = name.slice(0, 2);
  return `${visible}${"*".repeat(Math.max(name.length - 2, 3))}@${domain}`;
}

export function TestimonialsSection({ testimonials }: { testimonials: Testimonial[] }) {
  const trackRef = useRef<HTMLDivElement>(null);

  if (testimonials.length === 0) return null;

  function scrollByCard(direction: 1 | -1) {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector<HTMLElement>("[data-card]");
    const step = (card?.offsetWidth ?? track.clientWidth) + 20;
    track.scrollBy({ left: direction * step, behavior: "smooth" });
  }

  return (
    <section className="relative overflow-hidden bg-[#fce8ef] py-14 lg:py-20">
      <div className="relative mx-auto max-w-screen-xl px-6">
        <div className="mb-10 text-center">
          <p className="mb-2 text-xs font-bold uppercase tracking-widest text-[#b8305a]">
            Zaufali nam
          </p>
          <h2 className="font-[family-name:var(--font-playfair)] text-3xl leading-snug text-zinc-900 lg:text-4xl">
            Opinie{" "}
            <span
              className="font-script text-[#e0507a]"
              style={{ fontSize: "clamp(2rem, 3.5vw, 2.8rem)" }}
            >
              naszych klientów <span className="text-[#e0507a]/60">♡</span>
            </span>
          </h2>
        </div>

        <div className="relative">
          {testimonials.length > 1 && (
            <button
              type="button"
              onClick={() => scrollByCard(-1)}
              aria-label="Poprzednie opinie"
              className="absolute -left-4 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#e0507a] shadow-md transition-transform hover:scale-105 sm:flex"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}

          <div
            ref={trackRef}
            tabIndex={0}
            aria-label="Opinie klientów"
            className="motion-reduce:scroll-auto flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth px-1 py-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                data-card
                className="flex w-[85%] min-w-0 shrink-0 snap-start flex-col rounded-2xl bg-white p-6 shadow-sm sm:w-[calc((100%-1.25rem)/2)] lg:w-[calc((100%-2.5rem)/3)]"
              >
                <Quote className="mb-3 h-5 w-5 shrink-0 text-[#e0507a]/40" />
                <div className="mb-3 flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <Star
                      key={value}
                      className={`h-3.5 w-3.5 ${
                        value <= testimonial.rating
                          ? "fill-[#e0507a] text-[#e0507a]"
                          : "fill-transparent text-zinc-300"
                      }`}
                    />
                  ))}
                </div>
                <p className="mb-4 line-clamp-6 flex-1 text-sm leading-relaxed text-zinc-600">
                  {testimonial.content}
                </p>
                <p className="truncate text-xs font-bold uppercase tracking-widest text-zinc-400">
                  {maskEmail(testimonial.email)}
                </p>
              </div>
            ))}
          </div>

          {testimonials.length > 1 && (
            <button
              type="button"
              onClick={() => scrollByCard(1)}
              aria-label="Następne opinie"
              className="absolute -right-4 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#e0507a] shadow-md transition-transform hover:scale-105 sm:flex"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
